/** Structured client-side logs: a JSON ring buffer the user can upload to support. */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  ts: string;
  level: LogLevel;
  event: string;
  seq: number;
  [k: string]: unknown;
}

const REDACT = /token|authorization|secret|password|cookie/i;

export function redact(fields: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (REDACT.test(k)) out[k] = "[redacted]";
    else if (v instanceof Uint8Array) out[k] = `<${v.byteLength} bytes>`;
    else if (v instanceof Error) out[k] = { name: v.name, message: v.message };
    else out[k] = v;
  }
  return out;
}

export class RingLogger {
  private readonly buf: LogEntry[] = [];
  private seq = 0;
  readonly listeners = new Set<(e: LogEntry) => void>();

  constructor(
    readonly sessionId: string,
    private readonly capacity = 2000,
    private readonly now: () => Date = () => new Date(),
  ) {}

  log(level: LogLevel, event: string, fields: Record<string, unknown> = {}): LogEntry {
    const entry: LogEntry = { ts: this.now().toISOString(), level, event, seq: this.seq++, ...redact(fields) };
    this.buf.push(entry);
    if (this.buf.length > this.capacity) this.buf.splice(0, this.buf.length - this.capacity);
    for (const l of this.listeners) l(entry);
    if (level === "error") console.error(JSON.stringify(entry));
    else if (level === "warn") console.warn(JSON.stringify(entry));
    return entry;
  }
  debug(event: string, fields?: Record<string, unknown>): void {
    this.log("debug", event, fields);
  }
  info(event: string, fields?: Record<string, unknown>): void {
    this.log("info", event, fields);
  }
  warn(event: string, fields?: Record<string, unknown>): void {
    this.log("warn", event, fields);
  }
  error(event: string, fields?: Record<string, unknown>): void {
    this.log("error", event, fields);
  }

  snapshot(): LogEntry[] {
    return [...this.buf];
  }
  /** JSON Lines, for "download logs". */
  toJsonl(): string {
    return this.buf.map((e) => JSON.stringify(e)).join("\n") + "\n";
  }
}

export function newSessionId(): string {
  const b = crypto.getRandomValues(new Uint8Array(8));
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}
